/**
 * WCWN Size Guide Modal
 * Normalizes page reference data into the old working JSON structure.
 * Then uses proven rendering logic for SSENSE-style modal UI.
 */
(function () {
  'use strict';

  var OVERLAY_SEL  = '.wcwy-sg-overlay';
  var TRIGGER_SEL  = '.wcwy-sg-trigger';
  var CLOSE_SEL    = '[data-sg-close]';
  var PILL_SEL     = '.wcwy-sg-size-pill';
  var UNIT_SEL     = '.wcwy-sg-unit';

  var activeOverlay = null;

  // ── Helpers ──────────────────────────────────────────────────────────────

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function closest(el, sel) {
    if (el.closest) return el.closest(sel);
    while (el && el !== document) {
      if (el.matches && el.matches(sel)) return el;
      el = el.parentNode;
    }
    return null;
  }

  // ── Data Normalization ───────────────────────────────────────────────────

  function normalizeFieldName(raw) {
    var lower = raw.toLowerCase().trim();

    if (lower.includes('width') || lower.includes('chest') || lower.includes('bust')) {
      return 'chest';
    }
    if (lower.includes('height') || lower.includes('body length') || lower.includes('length')) {
      return 'body_length';
    }
    if (lower.includes('sleeve')) {
      return 'sleeve_length';
    }
    if (lower.includes('shoulder')) {
      return 'shoulder';
    }
    if (lower.includes('waist')) {
      return 'waist';
    }
    if (lower.includes('hip')) {
      return 'hip';
    }
    if (lower.includes('inseam')) {
      return 'inseam';
    }
    if (lower.includes('thigh')) {
      return 'thigh';
    }

    return lower.replace(/\s+/g, '_');
  }

  function parsePageContent(htmlContent) {
    var result = {
      sizes: [],
      fields: [],
      measurements: {},
      annotations: [],
      image: ''
    };

    // Extract image URL
    var imgMatch = htmlContent.match(/<img[^>]+src="([^"]+)"/i);
    if (imgMatch) {
      result.image = imgMatch[1];
    }

    // Extract table HTML
    var tableMatch = htmlContent.match(/<table[^>]*>([\s\S]*?)<\/table>/i);
    if (!tableMatch) {
      console.warn('[wcwy-sg] No table found in page content');
      return result;
    }

    var tableHtml = tableMatch[1];

    // Extract all rows
    var rowMatches = tableHtml.match(/<tr[^>]*>([\s\S]*?)<\/tr>/gi);
    if (!rowMatches || rowMatches.length === 0) {
      console.warn('[wcwy-sg] No rows found in table');
      return result;
    }

    // Process header row (first row)
    if (rowMatches.length > 0) {
      var headerRow = rowMatches[0];
      var headerCells = headerRow.match(/<th[^>]*>([\s\S]*?)<\/th>|<td[^>]*>([\s\S]*?)<\/td>/gi);

      if (!headerCells) {
        console.warn('[wcwy-sg] No header cells found');
        headerCells = [];
      }

      for (var i = 0; i < headerCells.length; i++) {
        var cell = headerCells[i];
        var text = cell.replace(/<[^>]*>/g, '').trim();
        if (text && text.toLowerCase() !== 'size' && text.toLowerCase() !== 'inseam') {
          result.fields.push(normalizeFieldName(text));
        }
      }
    }

    // Process data rows (skip header row)
    for (var i = 1; i < rowMatches.length; i++) {
      var row = rowMatches[i];
      var cells = row.match(/<td[^>]*>([\s\S]*?)<\/td>/gi);

      if (!cells || cells.length === 0) continue;

      var sizeLabel = cells[0].replace(/<[^>]*>/g, '').trim();
      if (!sizeLabel || sizeLabel.toLowerCase() === 'size') continue;

      result.sizes.push(sizeLabel);
      result.measurements[sizeLabel] = { in: [], cm: [] };

      // Extract measurement values from cells (skip first cell which is size)
      for (var j = 1; j < cells.length && j - 1 < result.fields.length; j++) {
        var cellContent = cells[j].replace(/<[^>]*>/g, '').trim();

        // Extract first number from cell (handles "36\"", "36 cm", just "36", etc.)
        var numMatch = cellContent.match(/^[\s]*([0-9.]+)/);
        if (numMatch) {
          var value = numMatch[1];

          // Check if this looks like cm (has cm suffix or very large number)
          if (cellContent.toLowerCase().indexOf('cm') !== -1 || parseFloat(value) > 100) {
            result.measurements[sizeLabel].cm.push(value);
            result.measurements[sizeLabel].in.push((parseFloat(value) / 2.54).toFixed(2));
          } else {
            result.measurements[sizeLabel].in.push(value);
            result.measurements[sizeLabel].cm.push((parseFloat(value) * 2.54).toFixed(1));
          }
        } else {
          // If no number found, use placeholder
          result.measurements[sizeLabel].in.push('—');
          result.measurements[sizeLabel].cm.push('—');
        }
      }
    }

    return result;
  }

  function getAnnotationsByType(pageTitle) {
    var lower = pageTitle.toLowerCase();

    if (lower.includes('jacket')) {
      return {
        fields: ['suit_size', 'chest'],
        annotations: [
          { field: 'suit_size', x: 18, y: 35 },
          { field: 'chest', x: 75, y: 28 }
        ]
      };
    }

    if (lower.includes('vest')) {
      return {
        fields: ['chest', 'body_length'],
        annotations: [
          { field: 'chest', x: 50, y: 32 },
          { field: 'body_length', x: 12, y: 75 }
        ]
      };
    }

    if (lower.includes('long coat')) {
      return {
        fields: ['chest'],
        annotations: [
          { field: 'chest', x: 75, y: 32 }
        ]
      };
    }

    if (lower.includes('pant')) {
      return {
        fields: ['waist', 'hip', 'inseam', 'thigh'],
        annotations: [
          { field: 'waist', x: 50, y: 15 },
          { field: 'hip', x: 50, y: 35 },
          { field: 'inseam', x: 25, y: 60 },
          { field: 'thigh', x: 75, y: 50 }
        ]
      };
    }

    if (lower.includes('chekanka') || lower.includes('cape')) {
      return {
        fields: ['shoulder', 'chest'],
        annotations: [
          { field: 'shoulder', x: 50, y: 15 },
          { field: 'chest', x: 50, y: 55 }
        ]
      };
    }

    return { fields: [], annotations: [] };
  }

  // ── Modal Control ─────────────────────────────────────────────────────────

  function openModal(overlay) {
    if (!overlay) return;
    if (overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
    }
    document.body.style.overflow = 'hidden';
    activeOverlay = overlay;
    // Force a reflow before changing aria-hidden to trigger CSS animation
    overlay.offsetHeight;
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-open');
  }

  function closeModal(overlay) {
    if (!overlay) return;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
    // Wait for animation to complete before removing from DOM and resetting overflow
    setTimeout(function() {
      if (activeOverlay === overlay) {
        document.body.style.overflow = '';
        activeOverlay = null;
      }
    }, 350);
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  function renderAnnotations(overlay, data) {
    var layer = overlay.querySelector('.wcwy-sg-anno-layer');
    if (!layer || !Array.isArray(data.annotations)) return;

    layer.innerHTML = '';
    data.annotations.forEach(function (anno) {
      var span = document.createElement('span');
      span.className = 'wcwy-sg-annotation';
      span.style.left = anno.x + '%';
      span.style.top = anno.y + '%';
      span.setAttribute('data-sg-anno-field', anno.field);
      layer.appendChild(span);
    });
  }

  function updateAnnotations(overlay, data, size, unit) {
    var annos = overlay.querySelectorAll('.wcwy-sg-annotation[data-sg-anno-field]');
    if (!annos.length) return;

    var fields = data.fields || [];
    var entry = data.measurements && data.measurements[size];
    var values = entry && entry[unit];

    annos.forEach(function (anno) {
      var field = anno.getAttribute('data-sg-anno-field');
      var fieldIdx = fields.indexOf(field);
      if (fieldIdx === -1) {
        anno.style.display = 'none';
        return;
      }

      var raw = (values && values[fieldIdx] != null) ? String(values[fieldIdx]) : '';
      if (raw === '' || raw === '—') {
        anno.style.display = 'none';
      } else {
        anno.style.display = '';
        var suffix = unit === 'in' ? '"' : ' cm';
        anno.textContent = raw + suffix;
      }
    });
  }

  function renderPanel(overlay, data) {
    console.log('[wcwy-sg] renderPanel called with data:', data);
    var sizeRow = overlay.querySelector('.wcwy-sg-size-row');
    var unitToggle = overlay.querySelector('.wcwy-sg-unit-toggle');
    var tableBody = overlay.querySelector('.wcwy-sg-table tbody');

    console.log('[wcwy-sg] Found elements:', { sizeRow: !!sizeRow, unitToggle: !!unitToggle, tableBody: !!tableBody });

    if (!sizeRow || !unitToggle || !tableBody) {
      console.error('[wcwy-sg] Could not find panel elements');
      return;
    }

    var sizes = Array.isArray(data.sizes) ? data.sizes : [];
    var fields = Array.isArray(data.fields) ? data.fields : [];

    console.log('[wcwy-sg] Rendering with sizes:', sizes, 'fields:', fields);

    // Size buttons
    sizeRow.innerHTML = '';
    sizes.forEach(function (sz, i) {
      console.log('[wcwy-sg] Creating button for size:', sz);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wcwy-sg-size-pill' + (i === 0 ? ' is-active' : '');
      btn.setAttribute('data-sg-size', esc(sz));
      btn.textContent = sz;
      btn.style.visibility = 'visible';
      btn.style.display = 'inline-flex';

      // Attach click handler directly
      btn.addEventListener('click', function(e) {
        console.log('[wcwy-sg] SIZE BUTTON CLICKED:', sz);
        e.preventDefault();
        e.stopPropagation();

        overlay.querySelectorAll(PILL_SEL).forEach(function (p) {
          p.classList.remove('is-active');
        });
        btn.classList.add('is-active');

        var activeUnit = overlay.querySelector(UNIT_SEL + '.is-active');
        var unit = activeUnit ? activeUnit.getAttribute('data-sg-unit') : 'in';
        console.log('[wcwy-sg] Showing measurements for size:', sz, 'unit:', unit);
        showMeasurements(overlay, data, sz, unit);
      });

      sizeRow.appendChild(btn);
    });

    console.log('[wcwy-sg] After rendering, sizeRow has', sizeRow.querySelectorAll(PILL_SEL).length, 'buttons');
    setTimeout(function() {
      console.log('[wcwy-sg] Buttons in DOM check:', document.querySelectorAll(PILL_SEL).length, 'total pill elements');
      var firstBtn = sizeRow.querySelector(PILL_SEL);
      if (firstBtn) {
        console.log('[wcwy-sg] First button visible?', firstBtn.offsetHeight > 0, 'clickable?', window.getComputedStyle(firstBtn).pointerEvents !== 'none');
      }
    }, 100);

    // Unit toggle
    unitToggle.innerHTML = '';
    ['in', 'cm'].forEach(function (unit, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wcwy-sg-unit' + (i === 0 ? ' is-active' : '');
      btn.setAttribute('data-sg-unit', unit);
      btn.textContent = unit.toUpperCase();
      btn.style.visibility = 'visible';

      // Attach click handler directly
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        overlay.querySelectorAll(UNIT_SEL).forEach(function (u) {
          u.classList.remove('is-active');
        });
        btn.classList.add('is-active');

        var activePill = overlay.querySelector(PILL_SEL + '.is-active');
        var sz = activePill ? activePill.getAttribute('data-sg-size') : (sizes[0] || '');
        showMeasurements(overlay, data, sz, unit);
      });

      unitToggle.appendChild(btn);
    });

    // Measurements table
    tableBody.innerHTML = '';
    fields.forEach(function (field) {
      var tr = document.createElement('tr');

      var tdName = document.createElement('td');
      tdName.className = 'wcwy-sg-field-name';
      tdName.textContent = field.replace(/_/g, ' ');
      tr.appendChild(tdName);

      var tdValue = document.createElement('td');
      tdValue.className = 'wcwy-sg-field-value';
      tdValue.textContent = '—';
      tr.appendChild(tdValue);

      tableBody.appendChild(tr);
    });

    var firstSize = sizes[0] || '';
    renderAnnotations(overlay, data);
    showMeasurements(overlay, data, firstSize, 'in');
  }

  function showMeasurements(overlay, data, size, unit) {
    console.log('[wcwy-sg] showMeasurements called:', { size, unit, measurements: data.measurements });
    var entry = data.measurements && data.measurements[size];
    console.log('[wcwy-sg] Entry for size', size, ':', entry);
    var values = entry && entry[unit];
    console.log('[wcwy-sg] Values for unit', unit, ':', values);

    var valueIndex = 0;
    var allCells = overlay.querySelectorAll('.wcwy-sg-table tbody tr');
    console.log('[wcwy-sg] Found', allCells.length, 'table rows');

    allCells.forEach(function (row) {
      var cell = row.querySelector('.wcwy-sg-field-value');
      if (!cell) return;

      var raw = (values && values[valueIndex] != null) ? String(values[valueIndex]) : '—';
      var suffix = raw === '—' ? '' : (unit === 'in' ? '"' : ' cm');
      console.log('[wcwy-sg] Setting row', valueIndex, 'to:', raw + suffix);
      cell.textContent = raw + suffix;
      valueIndex++;
    });

    updateAnnotations(overlay, data, size, unit);
  }

  // ── Event Delegation ──────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    // Open size guide
    var trigger = closest(e.target, TRIGGER_SEL);
    if (trigger) {
      var pid = trigger.getAttribute('data-sg-id');
      console.log('[wcwy-sg] Trigger clicked, pid:', pid);
      var overlay = pid ? document.getElementById('wcwy-sg-' + pid) : null;
      console.log('[wcwy-sg] Found overlay:', !!overlay);
      if (!overlay) return;

      if (!overlay._sgReady) {
        var dataEl = document.getElementById('wcwy-sg-json-' + pid);
        console.log('[wcwy-sg] Data element found:', !!dataEl);
        if (dataEl) {
          console.log('[wcwy-sg] Data element content:', dataEl.textContent);
          try {
            var rawData = JSON.parse(dataEl.textContent);
            var pageContent = dataEl.getAttribute('data-page-content') || '';

            // Decode HTML entities
            var textarea = document.createElement('textarea');
            textarea.innerHTML = pageContent;
            pageContent = textarea.value;

            var parsedData = parsePageContent(pageContent);
            var annotationData = getAnnotationsByType(rawData.pageTitle);

            // Merge annotation data with parsed data
            parsedData.annotations = annotationData.annotations;
            if (annotationData.fields && annotationData.fields.length > 0) {
              parsedData.fields = annotationData.fields;
            }

            renderPanel(overlay, parsedData);
            overlay._sgData = parsedData;
            overlay._sgReady = true;
          } catch (e) {
            console.error('[wcwy-sg] Error initializing size guide:', e);
          }
        } else {
          console.error('[wcwy-sg] Data element not found');
        }
      }

      openModal(overlay);
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Close button
    var closeBtn = closest(e.target, CLOSE_SEL);
    if (closeBtn) {
      var ov = closest(closeBtn, OVERLAY_SEL);
      if (ov) {
        closeModal(ov);
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    // Backdrop click (only if clicking the overlay itself, not modal content)
    if (e.target && e.target.classList && e.target.classList.contains('wcwy-sg-overlay')) {
      closeModal(e.target);
      return;
    }

    // Size pill (fallback - direct listeners are primary)
    var pill = closest(e.target, PILL_SEL);
    if (pill) {
      var ov = closest(pill, OVERLAY_SEL);
      if (!ov || !ov._sgData) return;

      ov.querySelectorAll(PILL_SEL).forEach(function (p) {
        p.classList.remove('is-active');
      });
      pill.classList.add('is-active');

      var activeUnit = ov.querySelector(UNIT_SEL + '.is-active');
      var unit = activeUnit ? activeUnit.getAttribute('data-sg-unit') : 'in';
      var size = pill.getAttribute('data-sg-size');
      showMeasurements(ov, ov._sgData, size, unit);

      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Unit button (fallback - direct listeners are primary)
    var unitBtn = closest(e.target, UNIT_SEL);
    if (unitBtn) {
      var ov = closest(unitBtn, OVERLAY_SEL);
      if (!ov || !ov._sgData) return;

      ov.querySelectorAll(UNIT_SEL).forEach(function (u) {
        u.classList.remove('is-active');
      });
      unitBtn.classList.add('is-active');

      var activePill = ov.querySelector(PILL_SEL + '.is-active');
      var sizes = ov._sgData.sizes || [];
      var size = activePill ? activePill.getAttribute('data-sg-size') : (sizes[0] || '');
      var unit = unitBtn.getAttribute('data-sg-unit');
      showMeasurements(ov, ov._sgData, size, unit);

      e.preventDefault();
      e.stopPropagation();
      return;
    }

  });

  // ── Escape key ────────────────────────────────────────────────────────────

  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.keyCode === 27) && activeOverlay) {
      closeModal(activeOverlay);
    }
  });

})();
